/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, Inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    EMPTY,
    finalize,
    Observable,
    Subject,
    switchMap,
    tap,
} from 'rxjs';
import { getPrivacyTypeLabel, PrivacyTypeEnum } from '../../../../constants/privacy.constant';
import { IUserListingFilterModel, IUserModel } from '../../../../models/user.model';
import { GroupsService } from '../../../../services/api/groups/groups.service';
import { UserService } from '../../../../services/api/user/user.service';
import { LoaderService } from '../../../../services/loader/loader.service';
import { IBasePopupModel } from '../../models/popup.model';
import { BasePopupComponent } from '../base-popup/base-popup.component';
import { SuccessPopupComponent } from '../success-popup/success-popup.component';

@Component({
    selector: 'app-group-invite-popup',
    standalone: false,
    templateUrl: './group-invite-popup.component.html',
    styleUrl: './group-invite-popup.component.scss',
})
export class GroupInvitePopupComponent extends BasePopupComponent {
    protected usersData = signal<IUserModel[]>([]);
    protected isInitialLoading = signal<boolean>(true); // For skeleton loader
    protected selectedUsers = signal<number[]>([]);
    protected PrivacyTypeEnum = PrivacyTypeEnum;
    protected getPrivacyTypeLabel = getPrivacyTypeLabel;
    private page = 1;
    private size = 20;
    private allLoaded = false;
    private searchSubject = new Subject<string>();
    private currentKeyword = '';

    constructor(
        @Inject(MAT_DIALOG_DATA) protected override data: any,
        protected override dialogRef: MatDialogRef<GroupInvitePopupComponent>,
        private userService: UserService,
        private loaderService: LoaderService,
        private groupsService: GroupsService,
        private dialog: MatDialog,
    ) {
        super(data, dialogRef);
    }

    static override getDialogConfig(): MatDialogConfig<IBasePopupModel> {
        return {
            maxWidth: '768px',
        };
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.setupSearchListener();
        this.loadUsers().subscribe();
    }

    protected onScroll(): void {
        this.loadUsers(false, { keyword: this.currentKeyword }).subscribe();
    }

    // Called on (input)
    protected onSearchInput(query: string): void {
        this.searchSubject.next(query);
    }

    protected loadUsers(reset = false, filter?: IUserListingFilterModel): Observable<IUserModel[]> {
        if (this.allLoaded && !reset) {
            return EMPTY;
        }

        if (reset) {
            this.page = 1;
            this.allLoaded = false;
            this.usersData.set([]);
            this.isInitialLoading.set(true);
        }

        this.loaderService.show();
        return this.userService
            .getUsersListing({ ...filter, page: this.page, size: this.size, notInGroup: this.data.groupId })
            .pipe(
                tap((response) => {
                    this.usersData.update((prev) => [...prev, ...response]);
                    if (response.length < this.size) {
                        this.allLoaded = true;
                    } else {
                        this.page++;
                    }
                }),
                catchError((error) => {
                    const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                    errorPopupConfig.data = {
                        title: `Error`,
                        message: error.error?.message,
                    };
                    this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                    console.error('Error getting users listing:', error);
                    return EMPTY;
                }),
                finalize(() => {
                    this.isInitialLoading.set(false);
                    this.loaderService.hide();
                }),
            );
    }

    protected toggleUser(userId: number, checked: boolean): void {
        this.selectedUsers.update((prev) => {
            if (checked) {
                return [...prev, userId]; // Add user
            } else {
                return prev.filter((id) => id !== userId); // Remove user
            }
        });
    }

    protected inviteSelectedUsers(): void {
        const users = this.selectedUsers();

        if (users.length === 0) {
            return;
        }

        this.loaderService.show();
        this.groupsService
            .inviteMember(this.data.groupId, users)
            .pipe(
                tap(() => {
                    const popupConfig = SuccessPopupComponent.getDialogConfig();
                    popupConfig.data = {
                        title: `Invitation Sent!`,
                        message: 'Thanks for spreading the word. You have successfully invited user(s) to the group.',
                        iconPath: '/assets/images/shared/success-green-icon.svg',
                        confirmButtonLabel: 'Done',
                    };
                    this.dialogRef.afterClosed().subscribe(() => {
                        this.dialog.open(SuccessPopupComponent, popupConfig);
                    });
                    this.dialogRef.close();
                }),
                catchError((error) => {
                    const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                    errorPopupConfig.data = {
                        title: `Error`,
                        message: error.error?.message,
                    };
                    this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                    console.error('Error inviting member:', error);
                    return EMPTY;
                }),
                finalize(() => {
                    this.loaderService.hide();
                }),
            )
            .subscribe();
    }

    // Debounced search logic
    private setupSearchListener(): void {
        this.searchSubject
            .pipe(
                debounceTime(400),
                distinctUntilChanged(),
                tap((keyword) => {
                    this.currentKeyword = keyword;
                }),
                switchMap((keyword) => {
                    this.selectedUsers.set([]);
                    return this.loadUsers(true, { keyword });
                }),
            )
            .subscribe(() => document.getElementById('searchInput')?.focus());
    }
}
